import { EntriesBuffer } from './plugins/lighthouse-metrics/utils/buffer/index.tsx';
import { PerformanceObserverEntryTypes } from './plugins/lighthouse-metrics/const.tsx';
import type { LayoutShift } from './plugins/lighthouse-metrics/utils/buffer/index.tsx';

type Rect = [left: number, top: number, right: number, bottom: number];

type LayoutShiftSource = {
	nodeRef: number | null;
	attribution: string;
	element: string;
	previousRect: Rect;
	currentRect: Rect;
	hasInsideShift: boolean;
	hasOutsideShift: boolean;
};

type LayoutShiftDetails = {
	shiftRef: number;
	time: number;
	value: number;
	hadRecentInput: boolean;
	sessionRef: number | null;
	inMaxSession: boolean;
	sources: LayoutShiftSource[];
};

export type LayoutShiftAttributionPayload = {
	version: 2;
	totalShift: number;
	shiftCount: number;
	bufferTruncated: boolean;
	shifts: LayoutShiftDetails[];
	/** Records omitted by the detail limits, not by includeEntries being disabled. */
	omittedShiftCount: number;
};

export type LayoutShiftAttributionConfig = {
	includeEntries?: Record<string, boolean>;
};

const MAX_REPORTED_SHIFTS = 50;
// JSON uses at most three UTF-8 bytes per UTF-16 code unit. Bound the detailed
// array to roughly 30 KB even for non-ASCII selectors, plus the small summary.
const MAX_DETAIL_CHARACTERS = 10_000;

/**
 * This is a reverse-engineered type for a React fiber node.
 * We're messing with React internals throughout this file, so it can blow up badly 🫠
 */
type ReactFiberNode = {
	stateNode: React.Component;
	child: ReactFiberNode | undefined;
	sibling: ReactFiberNode | undefined;
	return: ReactFiberNode | undefined;
	elementType?: React.ReactElement & { name?: string; displayName?: string };
	memoizedProps?: {
		attribution?: string;
		__boundaryMarker: boolean;
	};
};

function findFiberNode(domNode: HTMLElement | null): ReactFiberNode | undefined {
	if (!domNode) return undefined;

	const key = Object.keys(domNode).find((k) => k.startsWith('__reactFiber$'));

	return key ? (Reflect.get(domNode, key) as ReactFiberNode) : undefined;
}

function attributionByClosestFiberNode(fiberNode: ReactFiberNode) {
	let current: ReactFiberNode | undefined = fiberNode;
	while (current) {
		if (current.memoizedProps?.__boundaryMarker) {
			return current.memoizedProps?.attribution ?? 'unknown';
		}

		current = current.return;
	}

	return 'not-attributed';
}

function toQuerySelector(node: HTMLElement): string {
	const path = [];

	if (node instanceof HTMLElement) {
		if (node.tagName) {
			path.push(node.tagName.toLowerCase());
		}
		// DOM IDs can contain user-authored heading text. Never include them.
		if (node.className) {
			path.push(`.${node.className.split(' ').join('.')}`);
		}
		if (node.dataset) {
			Object.entries(node.dataset).forEach(([key, value]) => {
				path.push(`[data-${key}=${value}]`);
			});
		}
	}

	return path.join('');
}

function toRect(rect: DOMRectReadOnly): Rect {
	return [rect.left, rect.top, rect.right, rect.bottom];
}

function hasChangedRect({ previousRect, currentRect }: LayoutShift['sources'][number]): boolean {
	return (
		previousRect.x !== currentRect.x ||
		previousRect.y !== currentRect.y ||
		previousRect.width !== currentRect.width ||
		previousRect.height !== currentRect.height ||
		previousRect.top !== currentRect.top ||
		previousRect.right !== currentRect.right ||
		previousRect.bottom !== currentRect.bottom ||
		previousRect.left !== currentRect.left
	);
}

type ShiftRecord = {
	shift: LayoutShift;
	shiftRef: number;
	sessionRef: number | null;
};

function assignSessionWindows(records: ShiftRecord[]): number | null {
	let current: { ref: number; start: number; end: number; value: number } | undefined;
	let maxValue = 0;
	let maxSessionRef: number | null = null;

	for (const record of records) {
		const { shift } = record;
		// Match UFO's lighthouse-metrics/cls eligibility and burst boundaries.
		// UFO currently includes hadRecentInput entries; preserve that flag in
		// the details rather than silently applying a different metric policy.
		// The browser reports at most five sources per shift, so this scan is bounded.
		if (!shift.sources.some(hasChangedRect)) {
			continue;
		}
		const end = shift.startTime + shift.duration;
		if (!current || shift.startTime - current.end > 1000 || end - current.start > 5000) {
			current = { ref: (current?.ref ?? 0) + 1, start: shift.startTime, end, value: 0 };
		}
		current.end = end;
		current.value += shift.value;
		record.sessionRef = current.ref;
		if (current.value > maxValue) {
			maxValue = current.value;
			maxSessionRef = current.ref;
		}
	}
	return maxSessionRef;
}

export const getLayoutShiftWithAttribution = (
	experienceName: string,
	clsAttributionConfig: LayoutShiftAttributionConfig,
	{ start, end }: { start: number; end: number },
): LayoutShiftAttributionPayload => {
	const buffer = EntriesBuffer[PerformanceObserverEntryTypes.LayoutShift];
	const shifts = (buffer.buffer as LayoutShift[]).filter(
		(shift) => shift.startTime >= start && shift.startTime <= end,
	);
	const payload: LayoutShiftAttributionPayload = {
		version: 2,
		totalShift: shifts.reduce((total, shift) => total + shift.value, 0),
		shiftCount: shifts.length,
		bufferTruncated: buffer.full,
		shifts: [],
		omittedShiftCount: 0,
	};

	// Preserve the existing per-experience switch. No DOM/fiber traversal,
	// geometry serialization, or session analysis when details are disabled.
	if (!clsAttributionConfig.includeEntries?.[experienceName]) {
		return payload;
	}

	shifts.sort((a, b) => a.startTime - b.startTime);
	const records: ShiftRecord[] = shifts.map((shift, index) => ({
		shift,
		shiftRef: index + 1,
		sessionRef: null,
	}));
	const maxSessionRef = assignSessionWindows(records);
	const nodeRefs = new WeakMap<HTMLElement, number>();
	let nextNodeRef = 1;
	let detailCharacters = 2;

	// Prefer the largest shifts when the detail budget is exhausted. The
	// summary and window membership always use all retained in-window shifts.
	for (const { shift, shiftRef, sessionRef } of records
		.sort((a, b) => b.shift.value - a.shift.value)
		.slice(0, MAX_REPORTED_SHIFTS)) {
		const entry: LayoutShiftDetails = {
			shiftRef,
			time: shift.startTime - start,
			value: shift.value,
			hadRecentInput: shift.hadRecentInput,
			sessionRef,
			inMaxSession: sessionRef !== null && sessionRef === maxSessionRef,
			// At most five browser-provided sources for each of at most 50 selected shifts.
			sources: shift.sources.map((source) => {
				let nodeRef: number | null = null;
				if (source.node) {
					nodeRef = nodeRefs.get(source.node) ?? nextNodeRef++;
					nodeRefs.set(source.node, nodeRef);
				}
				const fiberNode = findFiberNode(source.node);
				return {
					nodeRef,
					attribution: fiberNode ? attributionByClosestFiberNode(fiberNode) : 'nonReact',
					element: source.node ? toQuerySelector(source.node) : 'undefined',
					previousRect: toRect(source.previousRect),
					currentRect: toRect(source.currentRect),
					hasInsideShift:
						source.previousRect.width !== source.currentRect.width ||
						source.previousRect.height !== source.currentRect.height,
					hasOutsideShift:
						source.previousRect.x !== source.currentRect.x ||
						source.previousRect.y !== source.currentRect.y,
				};
			}),
		};
		const characters = JSON.stringify(entry).length + 1;
		if (detailCharacters + characters <= MAX_DETAIL_CHARACTERS) {
			payload.shifts.push(entry);
			detailCharacters += characters;
		}
	}
	payload.shifts.sort((a, b) => a.shiftRef - b.shiftRef);
	payload.omittedShiftCount = shifts.length - payload.shifts.length;
	return payload;
};
